/**
 * O quadro da captação (`US-312`, `BR-006`, `TP-043`).
 *
 * Uma VISÃO de `/casos`, não uma tela. Lê a MESMA rota que a tabela — `GET
 * /casos?recorte=captacao` —, com o mesmo DTO e o mesmo mapeador; o que muda é
 * o recorte que o servidor aplica e a forma de desenhar.
 *
 * Não é o quadro de `/captacao-siai`: aquele mostra TICKET (pré-Caso) chegando
 * pela esteira, por outro endpoint. Este mostra Caso promovido, por substatus.
 *
 * Só leitura. Nenhum `POST`, nenhum `PATCH`, nenhum `onResolver`: quem move o
 * Caso é o detalhe, com o gatilho e a confirmação que ele já tem.
 */

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { formatarHora } from '../../shared/dominio/datas';
import { ORIGENS_DO_CASO, caminhoDoCaso } from '../../shared/navegacao/mapaDeNavegacao';
import { Board, BoardColuna } from '../../shared/ui/primitivas';
import { variaveisDaMarca } from '../../tema/variaveisDaMarca';
import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../shared/ui/EstadoDeErro';
import { CartaoDoCaso } from './CartaoDoCaso';
import { ETAPAS, ROTULOS_DE_ETAPA } from './etapas';
import {
  ORIGEM_DO_QUADRO,
  type OpcoesDeFiltro,
  REGRA_DA_PERMANENCIA,
  SEM_OPCOES,
  agruparPorEtapa,
  opcoesDeFiltro,
} from './leitura';
import { type FiltrosDaCaptacao, SEM_FILTRO, opcoesDaCaptacao } from './api/casos';
import type { CasoDaFila } from './api/tipos';

/** `''` no `<TextField select>` é "todos" — o valor vazio é o estado limpo. */
const TODOS = '';

export function VisaoEmQuadro(): React.JSX.Element {
  const navigate = useNavigate();
  const [filtros, definirFiltros] = useState<FiltrosDaCaptacao>(SEM_FILTRO);
  const consulta = useQuery(opcoesDaCaptacao(filtros));

  // O que o filtro oferece se acumula entre leituras: a leitura seguinte já
  // vem filtrada, e sem o acúmulo a pessoa ficaria presa no filtro que acabou
  // de aplicar — o núcleo que ela queria trocar teria sumido da lista.
  //
  // Ajuste de estado DURANTE o render, guardado pela comparação com a última
  // leitura vista — o padrão que o React documenta para derivar estado de uma
  // mudança. Num efeito, cada leitura renderizaria duas vezes; num ref, o
  // valor não participaria do render. `opcoesDeFiltro` é idempotente (união
  // de conjuntos), então reexecutá-la com a mesma leitura não muda nada.
  const [leituraVista, definirLeituraVista] = useState<readonly CasoDaFila[] | undefined>(undefined);
  const [escolhas, definirEscolhas] = useState<OpcoesDeFiltro>(SEM_OPCOES);
  if (consulta.data !== undefined && consulta.data !== leituraVista) {
    definirLeituraVista(consulta.data);
    definirEscolhas(opcoesDeFiltro(consulta.data, escolhas));
  }

  const abrirCaso = (casoId: string) => {
    void navigate(caminhoDoCaso(casoId), { state: { origem: ORIGENS_DO_CASO.captacao } });
  };

  const corpo = (() => {
    // F2: a PRIMEIRA leitura falhou e não há nada na tela. Mostra erro, nunca
    // "nenhum Caso" — a pessoa precisa distinguir "não consegui ler" de "não
    // há o que ler" sem perguntar a ninguém.
    if (consulta.isError && consulta.data === undefined) {
      return (
        <EstadoDeErro
          erro={consulta.error}
          contexto="Não foi possível ler o painel da captação."
          aoReprocessar={() => {
            void consulta.refetch();
          }}
        />
      );
    }

    if (consulta.data === undefined) return <EstadoDeCarregamento linhas={6} />;

    const grupos = agruparPorEtapa(consulta.data);
    const semEtapa = grupos.get('desconhecida') ?? [];
    const ultimaLeitura = formatarHora(new Date(consulta.dataUpdatedAt));

    return (
      <>
        {/* F1 e F5: a atualização falhou DEPOIS de já ter funcionado. O
            conteúdo anterior fica — substituí-lo por etapas vazias diria que
            a captação esvaziou —, mas marcado como velho e com a hora da
            última leitura boa, para a pessoa saber desde quando. */}
        {consulta.isError && (
          <Alert
            severity="warning"
            variant="outlined"
            role="alert"
            sx={{ mt: 3 }}
            action={
              <Button
                size="small"
                color="inherit"
                startIcon={<RefreshOutlined />}
                onClick={() => {
                  void consulta.refetch();
                }}
              >
                Tentar de novo
              </Button>
            }
          >
            Não foi possível atualizar o painel. Você está vendo a leitura das {ultimaLeitura}.
          </Alert>
        )}

        <p className="mt-6 text-[0.8125rem] text-[var(--tinta-3)]" aria-live="polite">
          {consulta.isError ? 'Desatualizado' : 'Última leitura'} às {ultimaLeitura}
          {consulta.isError ? null : ' · atualiza sozinho a cada 30 segundos'}
        </p>

        <Board>
          {ETAPAS.map((etapa) => {
            const casos = grupos.get(etapa) ?? [];
            return (
              <BoardColuna key={etapa} nome={ROTULOS_DE_ETAPA[etapa]} contagem={String(casos.length)}>
                {casos.length === 0 ? (
                  // F3: etapa vazia AFIRMA que a leitura ocorreu. Coluna em
                  // branco se confundiria com a tela que não carregou.
                  <p className="anery-ficha-meta">Nenhum Caso nesta etapa.</p>
                ) : (
                  casos.map((caso) => <CartaoDoCaso key={caso.id} caso={caso} aoAbrir={abrirCaso} />)
                )}
              </BoardColuna>
            );
          })}

          {/* F4: substatus fora do mapeamento não some — ganha lugar próprio.
              A coluna só existe quando há Caso nela: fora disso seria ruído. */}
          {semEtapa.length > 0 && (
            <BoardColuna nome={ROTULOS_DE_ETAPA.desconhecida} contagem={String(semEtapa.length)}>
              {semEtapa.map((caso) => (
                <CartaoDoCaso key={caso.id} caso={caso} aoAbrir={abrirCaso} />
              ))}
            </BoardColuna>
          )}
        </Board>

        <p className="mt-8 max-w-[68ch] text-[0.8125rem] leading-relaxed text-[var(--tinta-3)]">
          {REGRA_DA_PERMANENCIA}
        </p>
      </>
    );
  })();

  // As variaveis da marca vinham do `TelaAnery`; sem a casca, o invólucro
  // passa a ser daqui — o cartão e o quadro leem `--navy`, `--tinta-*` e os
  // tons do semáforo direto delas.
  return (
    <div className="anery-marca" style={variaveisDaMarca}>
      <p className="text-[0.8125rem] text-[var(--tinta-2)]">{ORIGEM_DO_QUADRO}</p>
      {/* S7: o filtro ESTREITA o que o escopo já permitiu. Ele vai na
          consulta, não no cliente — filtrar depois de receber traria dado de
          paciente que ninguém ia ver, e a trilha registraria a leitura. */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mt: 3 }}>
        <TextField
          select
          size="small"
          label="Núcleo"
          value={filtros.nucleo ?? TODOS}
          onChange={(evento) => {
            const valor = evento.target.value;
            definirFiltros((atual) => ({ ...atual, nucleo: valor === TODOS ? null : valor }));
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value={TODOS}>Todos os núcleos</MenuItem>
          {escolhas.nucleos.map((nucleo) => (
            <MenuItem key={nucleo.id} value={nucleo.id}>
              {nucleo.nome}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Operadora"
          value={filtros.operadora ?? TODOS}
          onChange={(evento) => {
            const valor = evento.target.value;
            definirFiltros((atual) => ({ ...atual, operadora: valor === TODOS ? null : valor }));
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value={TODOS}>Todas as operadoras</MenuItem>
          {escolhas.operadoras.map((operadora) => (
            <MenuItem key={operadora} value={operadora}>
              {operadora}
            </MenuItem>
          ))}
        </TextField>

        {(filtros.nucleo !== null || filtros.operadora !== null) && (
          <Button
            size="small"
            onClick={() => {
              definirFiltros(SEM_FILTRO);
            }}
          >
            Limpar filtros
          </Button>
        )}
      </Stack>

      {corpo}
    </div>
  );
}
